import { ApolloQueryResult } from '@apollo/client';
import { CheckCircle as CheckCircleIcon, WatchLater as WatchLaterIcon } from '@mui/icons-material';
import { Card, CardContent, CardHeader, Tooltip } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import * as WebTabs from 'react-web-tabs';
import semver from 'semver';
import { makeStyles } from 'tss-react/mui';

import { useTranslation } from '@/hooks';

import * as SensorConfigTab from './sensor-config-tab';
import * as WiFiConfigTab from './wifi-config-tab';

const useStyles = makeStyles()((theme) => ({
  card: {
    '& > div > div > div > div > table > colgroup > col:nth-child(6)': {
      width: '150px',
    },
    '& > div > div > div > div > table > colgroup > col:nth-child(5)': {
      width: '130px',
    },
    '& > div > div > div > div > table > colgroup > col:nth-child(4)': {
      width: '100px',
    },
    height: '100%',
  },
  flexRow: {
    display: 'flex',
    padding: theme.spacing(),
  },
  tabs: {
    '&[data-rwt-vertical="true"]': {
      display: 'flex',
    },
    display: 'inline-block',
    height: '100%',
  },
  tabList: {
    '&[aria-orientation="vertical"]': {
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      flexGrow: 0,
      borderRight: '1px solid #ddd',
      marginRight: '1rem',
    },
  },
  status: {
    marginLeft: theme.spacing(3),
    height: '20px',
  },
  pending: {
    color: 'orange',
  },
  sync: {
    color: 'green',
  },
}));

export const SyncBadge: React.FunctionComponent<{ pendingChanges: boolean }> = ({ pendingChanges }) => {
  const { classes, cx } = useStyles();
  const [t] = useTranslation();

  if (pendingChanges) {
    return (
      <Tooltip
        title={t(['device:configPending'], { defaultValue: 'Changes will be applied next time the device is online' })}
      >
        <WatchLaterIcon className={cx(classes.status, classes.pending)} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t(['device:configInSync'], { defaultValue: 'Configuration in sync with device' })}>
      <CheckCircleIcon className={cx(classes.status, classes.sync)} />
    </Tooltip>
  );
};

interface Properties {
  device: Schema.AdministrationDevicePeripheralsQuery['device'];
  refetchPeripherals: () => Promise<ApolloQueryResult<Schema.AdministrationDevicePeripheralsQuery>>;
  loading: boolean;
}

const DeviceConfigCard: React.FunctionComponent<Properties> = ({ device, refetchPeripherals, loading }) => {
  const { classes } = useStyles();
  const [t] = useTranslation();

  return (
    <Card elevation={1} className={classes.card} style={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t(['shared:deviceConfig'], { defaultValue: 'Device Config' })}
        titleTypographyProps={{ variant: 'h6', className: classes.flexRow }}
      />

      <CardContent style={{ flexGrow: 1 }}>
        <WebTabs.Tabs vertical className={classes.tabs}>
          <WebTabs.TabList className={classes.tabList} selection={{ isVertical: () => true }}>
            <SensorConfigTab.Tab />
            {device?.network?.wifi ? <WiFiConfigTab.Tab network={device.network} /> : null}
          </WebTabs.TabList>

          <SensorConfigTab.TabPanel
            refetchPeripherals={refetchPeripherals}
            peripherals={device?.peripherals || []}
            loading={loading}
            device={device ?? undefined}
          />
          {device?.network?.wifi ? (
            <WiFiConfigTab.TabPanel
              loading={loading}
              refetch={refetchPeripherals}
              network={device.network}
              applicationVersion={semver.parse(device.status.firmwareVersions?.application)}
            />
          ) : null}
        </WebTabs.Tabs>
      </CardContent>
    </Card>
  );
};
export default DeviceConfigCard;
